import { ReactNode, useId } from 'react';

/** One segment of a piecewise limit: a level that holds from `from` to `to`. */
export interface ChartLimit {
  from: number;
  to: number;
  y: number;
  label?: string;
}

/** A value drawn across a span, e.g. the maximum recorded inside a region's window. */
export interface ChartLevel extends ChartLimit {
  state?: 'pass' | 'fail';
}

interface LimitChartProps {
  /** the measured series, [x, yMin, yMax] ascending — a decimated capture keeps both edges of each point */
  points?: Array<[number, number, number]>;
  seriesLabel?: string;
  /** the piecewise limit, drawn as a dashed staircase over labelled, shaded spans */
  limits?: ChartLimit[];
  limitLabel?: string;
  /** a lower limit, where a value must stay between two: a second dashed staircase */
  lowerLimits?: ChartLimit[];
  levels?: ChartLevel[];
  levelsLabel?: string;
  /** the x window; data outside it is clipped and y rescales to what is visible */
  xMin?: number;
  xMax?: number;
  xLabel: string;
  yLabel: string;
  height?: number;
  /** what the chart shows, for a screen reader */
  ariaLabel: string;
  caption?: ReactNode;
  id?: string;
}

const WIDTH = 880;
const MARGIN = { top: 26, right: 16, bottom: 46, left: 60 };

/** Round-number ticks, about `count` of them, covering [lo, hi]. */
export function niceTicks(lo: number, hi: number, count = 6): number[] {
  if (!(hi > lo)) return [lo];
  const raw = (hi - lo) / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? 10 * magnitude;
  const ticks: number[] = [];
  for (let t = Math.ceil(lo / step) * step; t <= hi + step * 1e-9; t += step) ticks.push(Number(t.toPrecision(12)));
  return ticks;
}

const tickLabel = (t: number): string => String(Number(t.toPrecision(6))).replace('-', '−');

/**
 * A measured series against a piecewise limit — the over-current envelope
 * chart of an inrush test record. Static SVG in the report's ink: bands under
 * everything (context, not data), then the grid, the limit, the recorded
 * levels, and the series on top. Deterministic: same props, same drawing.
 */
export function LimitChart({ points = [], seriesLabel, limits = [], limitLabel = 'Limit', lowerLimits = [], levels = [], levelsLabel, xMin, xMax, xLabel, yLabel, height = 340, ariaLabel, caption, id }: LimitChartProps) {
  const clipId = useId();
  const plotW = WIDTH - MARGIN.left - MARGIN.right;
  const plotH = height - MARGIN.top - MARGIN.bottom;

  const allX = [...points.map((p) => p[0]), ...limits.flatMap((l) => [l.from, l.to]), ...lowerLimits.flatMap((l) => [l.from, l.to]), ...levels.flatMap((l) => [l.from, l.to])];
  const x0 = xMin ?? (allX.length ? Math.min(...allX) : 0);
  const x1 = xMax ?? (allX.length ? Math.max(...allX) : 1);
  const inX = (x: number) => x >= x0 && x <= x1;
  const overlaps = (s: ChartLimit) => s.to > x0 && s.from < x1;
  const visible = points.filter((p) => inX(p[0]));
  const clip = (all: ChartLimit[]) => all.filter(overlaps).map((s) => ({ ...s, from: Math.max(s.from, x0), to: Math.min(s.to, x1) })).sort((a, b) => a.from - b.from);
  const segs = clip(limits);
  const lowerSegs = clip(lowerLimits);
  const lvls = levels.filter(overlaps).map((s) => ({ ...s, from: Math.max(s.from, x0), to: Math.min(s.to, x1) }));
  const ys = [...visible.flatMap((p) => [p[1], p[2]]), ...segs.map((s) => s.y), ...lowerSegs.map((s) => s.y), ...lvls.map((l) => l.y)];
  const empty = ys.length === 0 || !(x1 > x0);

  const yLo = Math.min(0, ...(ys.length ? ys : [0]));
  const yTop = Math.max(...(ys.length ? ys : [1]));
  const yHi = yTop > yLo ? yTop + (yTop - yLo) * 0.08 : yLo + 1;
  const sx = (x: number) => MARGIN.left + ((x - x0) / (x1 - x0 || 1)) * plotW;
  const sy = (y: number) => MARGIN.top + plotH - ((y - yLo) / (yHi - yLo)) * plotH;
  const f = (n: number) => n.toFixed(1);

  const staircaseOf = (steps: ChartLimit[]) => steps
    .map((s, i) => {
      const prev = steps[i - 1];
      const joined = prev && Math.abs(prev.to - s.from) < (x1 - x0) * 1e-6;
      return `${joined ? `V${f(sy(s.y))}` : `M${f(sx(s.from))},${f(sy(s.y))}`}H${f(sx(s.to))}`;
    })
    .join('');
  const staircase = staircaseOf(segs);
  const lowerStaircase = staircaseOf(lowerSegs);
  const hasBand = visible.some((p) => p[1] !== p[2]);
  const upper = visible.map((p, i) => `${i === 0 ? 'M' : 'L'}${f(sx(p[0]))},${f(sy(p[2]))}`).join('');
  const band = hasBand ? `${upper}${visible.slice().reverse().map((p) => `L${f(sx(p[0]))},${f(sy(p[1]))}`).join('')}Z` : '';

  // band captions share one lane along the top and drop out rather than print over each other
  let captionRight = -Infinity;
  const captions = segs.flatMap((s) => {
    if (!s.label) return [];
    const mid = (sx(s.from) + sx(s.to)) / 2;
    const half = (s.label.length * 5.6) / 2;
    if (mid - half < captionRight + 4 || sx(s.to) - sx(s.from) < half * 2 - 8) return [];
    captionRight = mid + half;
    return [{ x: mid, label: s.label }];
  });

  return (
    <figure className="report-chart" id={id}>
      <svg viewBox={`0 0 ${WIDTH} ${height}`} role="img" aria-label={ariaLabel} preserveAspectRatio="xMidYMid meet">
        <defs>
          <clipPath id={clipId}><rect x={MARGIN.left} y={MARGIN.top} width={plotW} height={plotH} /></clipPath>
        </defs>
        {empty ? (
          <text x={WIDTH / 2} y={height / 2} textAnchor="middle" className="chart-empty">No data to chart</text>
        ) : (
          <>
            {segs.map((s, i) => (
              <rect key={`band-${i}`} x={f(sx(s.from))} y={MARGIN.top} width={f(Math.max(0, sx(s.to) - sx(s.from)))} height={plotH} className={i % 2 ? 'chart-band alt' : 'chart-band'} />
            ))}
            {captions.map((c) => <text key={`cap-${c.label}`} x={f(c.x)} y={MARGIN.top - 8} textAnchor="middle" className="chart-band-label">{c.label}</text>)}
            {niceTicks(yLo, yHi).map((t) => (
              <g key={`y-${t}`}>
                <line x1={MARGIN.left} x2={MARGIN.left + plotW} y1={f(sy(t))} y2={f(sy(t))} className="chart-grid" />
                <text x={MARGIN.left - 6} y={f(sy(t) + 3)} textAnchor="end" className="chart-tick">{tickLabel(t)}</text>
              </g>
            ))}
            {niceTicks(x0, x1, 8).map((t) => (
              <g key={`x-${t}`}>
                <line x1={f(sx(t))} x2={f(sx(t))} y1={MARGIN.top + plotH} y2={MARGIN.top + plotH + 4} className="chart-axis" />
                <text x={f(sx(t))} y={MARGIN.top + plotH + 16} textAnchor="middle" className="chart-tick">{tickLabel(t)}</text>
              </g>
            ))}
            <line x1={MARGIN.left} x2={MARGIN.left + plotW} y1={MARGIN.top + plotH} y2={MARGIN.top + plotH} className="chart-axis" />
            <line x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + plotH} className="chart-axis" />
            <g clipPath={`url(#${clipId})`}>
              {staircase && <path d={staircase} className="chart-limit" />}
              {lowerStaircase && <path d={lowerStaircase} className="chart-limit" />}
              {lvls.map((l, i) => <line key={`lvl-${i}`} x1={f(sx(l.from))} x2={f(sx(l.to))} y1={f(sy(l.y))} y2={f(sy(l.y))} className={`chart-level ${l.state ?? ''}`} />)}
              {band && <path d={band} className="chart-series-band" />}
              {upper && <path d={upper} className="chart-series" />}
            </g>
            <text x={MARGIN.left + plotW / 2} y={height - 8} textAnchor="middle" className="chart-axis-title">{xLabel}</text>
            <text transform={`translate(14 ${MARGIN.top + plotH / 2}) rotate(-90)`} textAnchor="middle" className="chart-axis-title">{yLabel}</text>
          </>
        )}
      </svg>
      <div className="chart-legend">
        {visible.length > 0 && <span><i className="swatch series" />{seriesLabel ?? yLabel}</span>}
        {(segs.length > 0 || lowerSegs.length > 0) && <span><i className="swatch limit" />{limitLabel}</span>}
        {lvls.length > 0 && levelsLabel && <span><i className="swatch level" />{levelsLabel}</span>}
      </div>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}
